import { apiClient } from './client'
import type { ApiResponse } from './client'
import type { CompanyLookupViewDto } from './companies'
import type { ContactVisitorTimelineDto } from '@/types/analytics'

export interface SchoolLookupViewDto {
  Id: string
  LinkedInId: string
  Name?: string | null
  NormalizedName?: string | null
  UniversalName?: string | null
  LinkedInUrl?: string | null
  Tagline?: string | null
  Description?: string | null
  Type?: string | null
  Phone?: string | null
  StaffCount?: number | null
  StaffCountRange?: string | null
  FollowerCount?: number | null
  FoundedYear?: number | null
  CrunchbaseUrl?: string | null
  PageVerification?: string | null
  Website?: string | null
  Logo?: string | null
  Cover?: string | null
  Industries?: string[] | null
  Specialities?: string[] | null
}

export interface ContactPositionViewDto {
  CompanyId: string
  Title?: string | null
  Description?: string | null
  EmploymentType?: string | null
  Location?: string | null
  StartDate?: string | null // at LinkedIn's precision: "2019", "2019-03" or "2019-03-15"
  EndDate?: string | null // the same; null for a job held now
}

export interface ContactEducationViewDto {
  SchoolId: string
  Degree?: string | null
  FieldOfStudy?: string | null
  Grade?: string | null
  StartDate?: string | null // at LinkedIn's precision: "2019", "2019-03" or "2019-03-15"
  EndDate?: string | null // the same
}

export interface ContactSkillDto {
  Name?: string | null
  PassedSkillAssessment?: boolean | null
  EndorsementsCount?: number | null
}

export interface ContactLookupViewDto {
  Id: string
  LinkedInId: string
  LastEnrichedUtc: string // ISO8601
  NeedsEnrichment: boolean
  Username?: string | null
  FirstName?: string | null
  LastName?: string | null
  FullName?: string | null
  Phone?: string | null
  PhoneLastEnrichedDate?: string | null // ISO8601
  ProfilePicUrl?: string | null
  Summary?: string | null
  LinkedInHeadline?: string | null
  LinkedInDescription?: string | null
  LinkedInFollowersCount?: number | null
  City?: string | null
  State?: string | null
  Country?: string | null
  CountryCode?: string | null
  Location?: string | null
  IsPremium?: boolean | null
  IsCreator?: boolean | null
  Companies?: CompanyLookupViewDto[] | null
  Schools?: SchoolLookupViewDto[] | null
  Positions?: ContactPositionViewDto[] | null
  Education?: ContactEducationViewDto[] | null
  Skills?: ContactSkillDto[] | null
}

export const contactsApi = {
  lookupByLinkedInUrl: (
    linkedInUrl: string,
    opts?: { includeCompanies?: boolean; includeSchools?: boolean },
    signal?: AbortSignal
  ): Promise<ApiResponse<ContactLookupViewDto>> => {
    const encoded = encodeURIComponent(linkedInUrl.trim())
    const params: string[] = [`linkedinUrl=${encoded}`]
    const includes: string[] = []
    if (opts?.includeCompanies) includes.push('companies')
    if (opts?.includeSchools) includes.push('schools')
    if (includes.length) params.push(`includes=${includes.join(',')}`)
    const qs = params.join('&')
    return apiClient.get(`/api/Contacts/lookup?${qs}`, { signal })
  },

  lookupByContactId: (
    contactId: string,
    opts?: { includeCompanies?: boolean; includeSchools?: boolean }
  ): Promise<ApiResponse<ContactLookupViewDto>> => {
    const safeId = encodeURIComponent(contactId.trim())
    const includes: string[] = []
    if (opts?.includeCompanies) includes.push('companies')
    if (opts?.includeSchools) includes.push('schools')
    const qs = includes.length ? `?includes=${includes.join(',')}` : ''
    return apiClient.get(`/api/Contacts/${safeId}/lookup${qs}`)
  },

  /**
   * ORB-373: per-Contact visitor timeline — session items with expandable page-view
   * detail plus rollups (total visits, first/last seen, top LPs, conversion count).
   * Scoped to the workspace (the auth + partition boundary for visitor data).
   */
  getVisitorTimeline: (
    workspaceId: string,
    contactId: string
  ): Promise<ApiResponse<ContactVisitorTimelineDto>> => {
    const safeWorkspace = encodeURIComponent(workspaceId)
    const safeContact = encodeURIComponent(contactId)
    return apiClient.get(`/api/Workspaces/${safeWorkspace}/Contacts/${safeContact}/VisitorTimeline`)
  }
}
